import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

const FIELDS = [
  { key: "name", label: "FULL NAME", testId: "checkout-name-input", type: "text" },
  { key: "email", label: "EMAIL", testId: "checkout-email-input", type: "email" },
  { key: "institution", label: "INSTITUTION / LAB (OPTIONAL)", testId: "checkout-institution-input", type: "text" },
  { key: "referral", label: "REFERRAL CODE (OPTIONAL)", testId: "checkout-referral-input", type: "text" },
  { key: "address", label: "STREET ADDRESS", testId: "checkout-address-input", type: "text" },
  { key: "city", label: "CITY", testId: "checkout-city-input", type: "text" },
  { key: "state", label: "STATE", testId: "checkout-state-input", type: "text" },
  { key: "zip", label: "ZIP CODE", testId: "checkout-zip-input", type: "text" },
];

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const [form, setForm] = useState({ name: "", email: "", institution: "", address: "", city: "", state: "", zip: "", referral: "" });
  const [order, setOrder] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");
  const [refInfo, setRefInfo] = useState(null);

  const discount = refInfo?.valid ? total * 0.10 : 0;
  const finalTotal = total - discount;

  const validateReferral = async () => {
    const code = form.referral.trim();
    if (!code) {
      setRefInfo(null);
      return;
    }
    try {
      const res = await api.post("/referral-codes/validate", { code });
      setRefInfo(res.data);
    } catch {
      setRefInfo({ valid: false });
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    setError("");
    try {
      const res = await api.post("/orders", {
        customer: form,
        items: items.map((i) => ({ product_id: i.productId, dosage: i.dosage, qty: i.qty })),
      });
      setOrder(res.data);
      clearCart();
      toast.success("Order placed successfully");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to place order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (order) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pt-24">
        <motion.div
          data-testid="order-confirmation"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
          className="w-full max-w-lg border border-white/15 bg-[#0A0A0A] p-10 text-center"
        >
          <CheckCircle2 className="mx-auto h-12 w-12 text-[#00ff72]" />
          <p className="mt-6 font-mono text-[10px] tracking-[0.35em] text-[#A3A3A3]">ORDER REQUEST RECEIVED</p>
          <h1 className="mt-2 font-display text-4xl text-[#F9F6F0]">Thank you, researcher.</h1>
          <p className="mt-4 font-mono text-xs tracking-widest text-[#666666]">
            ORDER ID: <span data-testid="order-id" className="text-[#00ff72]">{order.id.slice(0, 8).toUpperCase()}</span>
          </p>
          <div className="mt-6 space-y-2 border-t border-white/10 pt-6 text-left font-mono text-xs">
            {order.items.map((i) => (
              <div key={`${i.product_id}-${i.dosage}`} className="flex justify-between text-[#A3A3A3]">
                <span>{i.name} {i.dosage} x{i.qty}</span>
                <span>${(i.price * i.qty).toFixed(2)}</span>
              </div>
            ))}
            {order.discount > 0 && (
              <div className="flex justify-between text-[#00ff72]">
                <span>REFERRAL DISCOUNT ({order.customer.referral} — 10%)</span>
                <span data-testid="order-discount">-${order.discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-white/10 pt-3 text-[#F9F6F0]">
              <span>TOTAL</span>
              <span data-testid="order-total">${order.total.toFixed(2)}</span>
            </div>
          </div>
          <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-wider text-[#666666]">
            NO PAYMENT WAS TAKEN. WE&apos;LL EMAIL {order.customer.email.toUpperCase()} TO ARRANGE
            PAYMENT AND CONFIRM YOUR ORDER.
          </p>
          <Link
            to="/products"
            data-testid="continue-shopping-link"
            className="group mt-8 inline-flex items-center gap-3 bg-[#00ff72] px-8 py-4 font-mono text-xs tracking-[0.2em] text-black transition-colors hover:bg-[#F9F6F0]"
          >
            CONTINUE RESEARCH
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </main>
    );
  }

  if (items.length === 0) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 pt-24">
        <p data-testid="checkout-empty-state" className="font-display text-4xl text-[#A3A3A3]">Your cart is empty</p>
        <Link to="/products" data-testid="browse-catalog-link" className="font-mono text-xs tracking-[0.2em] text-[#00ff72]">
          &#8592; BROWSE THE CATALOG
        </Link>
      </main>
    );
  }

  return (
    <main className="pt-24">
      <div className="mx-auto max-w-7xl px-6 py-12">
        <p className="font-mono text-[11px] tracking-[0.35em] text-[#00ff72]">SECURE CHECKOUT</p>
        <h1 className="mt-3 font-display text-5xl tracking-tight text-[#F9F6F0] md:text-7xl">
          Complete <span className="italic text-[#666666]">Order</span>
        </h1>

        <form onSubmit={placeOrder} className="mt-12 grid gap-12 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-[#666666]">
                  {f.label}
                </label>
                <input
                  data-testid={f.testId}
                  type={f.type}
                  required={f.key !== "institution" && f.key !== "referral"}
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  onBlur={f.key === "referral" ? validateReferral : undefined}
                  className="w-full border border-white/15 bg-transparent px-4 py-4 font-mono text-sm text-[#F9F6F0] focus:border-[#00ff72] focus:outline-none"
                />
                {f.key === "referral" && refInfo?.valid === true && (
                  <p data-testid="referral-valid-msg" className="mt-2 font-mono text-[10px] tracking-[0.2em] text-[#00ff72]">
                    CODE APPLIED — 10% OFF (THANKS TO {refInfo.referrer?.toUpperCase()})
                  </p>
                )}
                {f.key === "referral" && refInfo?.valid === false && form.referral.trim() && (
                  <p data-testid="referral-invalid-msg" className="mt-2 font-mono text-[10px] tracking-[0.2em] text-red-400">
                    CODE NOT RECOGNIZED — YOU CAN STILL ORDER WITHOUT IT
                  </p>
                )}
              </div>
            ))}
            <div className="border border-white/10 bg-[#0A0A0A] p-5">
              <p className="flex items-start gap-2 font-mono text-[10px] leading-relaxed tracking-wider text-[#A3A3A3]">
                <Lock className="mt-0.5 h-3 w-3 shrink-0 text-[#00ff72]" />
                NO PAYMENT IS TAKEN ON THIS SITE. AFTER YOU PLACE YOUR ORDER, SFH BIOHEALTH &amp;
                WELLNESS WILL EMAIL YOU TO ARRANGE PAYMENT SECURELY.
              </p>
            </div>
            {error && (
              <p data-testid="checkout-error" className="border border-red-500/40 bg-red-500/10 p-4 font-mono text-xs text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="place-order-btn"
              type="submit"
              disabled={placing}
              className="w-full bg-[#00ff72] px-8 py-5 font-mono text-xs tracking-[0.25em] text-black transition-colors hover:bg-[#F9F6F0] disabled:opacity-50"
            >
              {placing ? "SENDING ORDER..." : `SEND ORDER REQUEST — $${finalTotal.toFixed(2)}`}
            </button>
          </div>

          <aside className="lg:col-span-2">
            <div className="border border-white/10 bg-[#0A0A0A] p-6">
              <p className="font-mono text-[10px] tracking-[0.3em] text-[#A3A3A3]">ORDER SUMMARY</p>
              <ul className="mt-5 space-y-4">
                {items.map((i) => (
                  <li key={i.key} className="flex items-center gap-4">
                    <img src={i.image} alt={i.name} className="h-12 w-12 object-cover grayscale" />
                    <div className="flex-1">
                      <p className="text-sm text-[#F9F6F0]">{i.name}</p>
                      <p className="font-mono text-[10px] text-[#666666]">{i.dosage} x{i.qty}</p>
                    </div>
                    <span className="font-mono text-xs text-[#A3A3A3]">${(i.price * i.qty).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 space-y-2 border-t border-white/10 pt-5">
                {discount > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-[#666666]">SUBTOTAL</span>
                      <span className="font-mono text-xs text-[#A3A3A3]">${total.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-[#00ff72]">REFERRAL — 10% OFF</span>
                      <span data-testid="checkout-discount" className="font-mono text-xs text-[#00ff72]">-${discount.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between pt-1">
                  <span className="font-mono text-xs tracking-[0.2em] text-[#A3A3A3]">TOTAL</span>
                  <span data-testid="checkout-total" className="font-display text-2xl text-[#F9F6F0]">${finalTotal.toFixed(2)}</span>
                </div>
              </div>
              <p className="mt-5 font-mono text-[9px] leading-relaxed tracking-wider text-red-400/80">
                FOR RESEARCH USE ONLY — NOT FOR HUMAN CONSUMPTION. BY ORDERING YOU CONFIRM RESEARCH
                AFFILIATION.
              </p>
            </div>
          </aside>
        </form>
      </div>
    </main>
  );
}
